import { useToast } from '../../contexts/ToastContext';
import { friendlyLabel } from '../../utils/labels';
import React, { useState, useEffect } from 'react';
import { X, Save, Building2, Users, Bell, Shield } from 'lucide-react';
import { usersService } from '../../services/users';
import { Facility, FacilityUpdate, User } from '../../types';
import { facilitiesService } from '../../services/facilities';
import './FacilitySettingsModal.css';

interface FacilitySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  facility: Facility | null;
  onDeleted?: () => void;
  onSave: (settings: FacilitySettings) => void | Promise<void>;
}

export interface FacilitySettings {
  // General settings
  allowSubAssets: boolean;
  requireLocationForAssets: boolean;
  autoGenerateAssetIds: boolean;

  // Inspection settings
  requirePhotosOnInspection: boolean;
  allowOfflineInspections: boolean;
  minCheckpointCompletionRate: number; // percentage

  // Notification settings
  notifyOnAssetAdded: boolean;
  notifyOnInspectionComplete: boolean;
  notifyOnHighRiskFindings: boolean;

  // Access control
  restrictAssetEditing: boolean;
  requireApprovalForAssetDeletion: boolean;
}

const DEFAULT_SETTINGS: FacilitySettings = {
  allowSubAssets: true,
  requireLocationForAssets: true,
  autoGenerateAssetIds: false,
  requirePhotosOnInspection: false,
  allowOfflineInspections: true,
  minCheckpointCompletionRate: 80,
  notifyOnAssetAdded: false,
  notifyOnInspectionComplete: true,
  notifyOnHighRiskFindings: true,
  restrictAssetEditing: false,
  requireApprovalForAssetDeletion: true,
};

const FacilitySettingsModal: React.FC<FacilitySettingsModalProps> = ({
  isOpen,
  onClose,
  facility,
  onSave,
  onDeleted
}) => {
  const { showSuccess } = useToast();
  const [settings, setSettings] = useState<FacilitySettings>(DEFAULT_SETTINGS);
  const [activeSection, setActiveSection] = useState<'general' | 'inspection' | 'notifications' | 'access'>('general');
  const [category,setCategory] = useState(facility?.facility_category || '');
  const [jurisdiction,setJurisdiction] = useState(facility?.jurisdiction || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmationName, setConfirmationName] = useState('');
  const [summary, setSummary] = useState<{assets: number; inspections: number; actions: number} | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [managerId, setManagerId] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [managerError, setManagerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !facility) return;

    setConfirmDelete(false);
    setConfirmationName('');
    setSummary(null);
    setDeleteError('');
    setName(facility.name);
    setAddress(facility.address || '');
    setDescription(facility.description || '');
    setManagerId(facility.manager_id || '');
    setSaveError(null);
    setManagerError(null);
    setUsers([]);
    setCategory(facility.facility_category || '');
    setJurisdiction(facility.jurisdiction || '');
    let cancelled = false;
    usersService.getUsers().then(currentUsers => {
      if (!cancelled) setUsers(currentUsers.filter(user => user.client_id === facility.client_id));
    }).catch(() => {
      if (!cancelled) setManagerError('Failed to load facility managers.');
    });
    facilitiesService
      .getSettings(facility.id)
      .then(currentSettings => {
        if (!cancelled) setSettings(currentSettings);
      })
      .catch(() => {
        if (!cancelled) setSettings(DEFAULT_SETTINGS);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, facility]);

  if (!isOpen || !facility) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      setSaveError('Facility name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const changes: FacilityUpdate = { facility_category: category || null, jurisdiction: jurisdiction || null };
      if (name.trim() !== facility.name) changes.name = name.trim();
      if (address !== (facility.address || '')) changes.address = address;
      if (description !== (facility.description || '')) changes.description = description;
      if (managerId !== (facility.manager_id || '')) changes.manager_id = managerId;
      await facilitiesService.updateFacility(facility.id, changes);
      await onSave(settings);
      showSuccess('Facility settings saved');
      onClose();
    } catch (e) {
      setSaveError(
        e instanceof Error ? e.message : 'Failed to save settings. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <K extends keyof FacilitySettings>(key: K, value: FacilitySettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content facility-settings-modal">
        <div className="modal-header">
          <div>
            <h2>Facility Settings</h2>
            <p className="facility-settings-subtitle">{facility.name}</p>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {onDeleted && (
          <div className="settings-section">
            {!confirmDelete ? <button className="btn btn-danger" onClick={async () => {
              setConfirmDelete(true);
              setDeleteError('');
              try { setSummary(await facilitiesService.getDeletionSummary(facility.id)); }
              catch (error) { setDeleteError('Unable to load deletion details. Close and try again.'); }
            }}>Delete facility</button> : <div role="dialog" aria-label="Delete facility">
              <h3>Delete {facility.name}?</h3>
              {summary && <p>This permanently removes {summary.assets} assets, {summary.inspections} inspections, {summary.actions} actions, lab history, documents, plans and staff links.</p>}
              <label>Type the facility name to confirm
                <input value={confirmationName} disabled={deleting} onChange={e => setConfirmationName(e.target.value)} />
              </label>
              {deleteError && <p role="alert">{deleteError}</p>}
              <button className="btn btn-danger" disabled={!summary || confirmationName !== facility.name || deleting} onClick={async () => {
                setDeleting(true);
                setDeleteError('');
                try {
                  await facilitiesService.deleteFacility(facility.id);
                  showSuccess('Facility deleted');
                  onDeleted();
                } catch (error) {
                  setDeleteError(error instanceof Error ? error.message : 'Failed to delete facility');
                } finally { setDeleting(false); }
              }}>{deleting ? 'Deleting…' : 'Permanently delete facility'}</button>
              <button className="btn btn-ghost" disabled={deleting} onClick={() => {setConfirmDelete(false); setConfirmationName(''); setSummary(null);}}>Cancel deletion</button>
            </div>}
          </div>
        )}
        <div className="facility-settings-body">
          <div className="settings-sidebar">
            <button
              className={`settings-nav-item ${activeSection === 'general' ? 'active' : ''}`}
              onClick={() => setActiveSection('general')}
            >
              <Building2 size={18} />
              <span>General</span>
            </button>
            <button
              className={`settings-nav-item ${activeSection === 'inspection' ? 'active' : ''}`}
              onClick={() => setActiveSection('inspection')}
            >
              <Shield size={18} />
              <span>Inspection</span>
            </button>
            <button
              className={`settings-nav-item ${activeSection === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveSection('notifications')}
            >
              <Bell size={18} />
              <span>Notifications</span>
            </button>
            <button
              className={`settings-nav-item ${activeSection === 'access' ? 'active' : ''}`}
              onClick={() => setActiveSection('access')}
            >
              <Users size={18} />
              <span>Access Control</span>
            </button>
          </div>

          <div className="settings-content">
            {activeSection === 'general' && (
              <div className="settings-section">
                <h3>Details</h3>
                <div className="facility-details-fields">
                  <label>Facility name<input required value={name} onChange={e => setName(e.target.value)} /></label>
                  <label>Address<input value={address} onChange={e => setAddress(e.target.value)} /></label>
                  <label>Description<textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} /></label>
                  <label>Facility manager<select value={managerId} onChange={e => setManagerId(e.target.value)}>
                    <option value="" disabled>Select a manager</option>
                    {managerId && !users.some(user => user.id === managerId) && <option value={managerId}>Current manager</option>}
                    {users.map(user => <option key={user.id} value={user.id}>{user.name} ({user.email})</option>)}
                  </select></label>
                  {managerError && <p role="alert">{managerError}</p>}
                </div>
                <h3>General Settings</h3>
                <label>Facility category<select value={category} onChange={e=>setCategory(e.target.value)}><option value="">Not specified</option>{['healthcare','aged_care','childcare','hotel','commercial','industrial','residential','other'].map(c=><option key={c} value={c}>{friendlyLabel(c)}</option>)}</select></label>
                <label>Jurisdiction<select value={jurisdiction} onChange={e=>setJurisdiction(e.target.value)}><option value="">Not specified</option>{['NSW','VIC','QLD','SA','WA','TAS','ACT','NT','NZ'].map(j=><option key={j}>{j}</option>)}</select></label>
                <p className="section-description">Configure basic facility and asset management preferences</p>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Allow Sub-Assets</label>
                    <p className="setting-description">Enable creation of hierarchical asset structures (assets within assets)</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.allowSubAssets}
                    onChange={(e) => updateSetting('allowSubAssets', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Require Location for Assets</label>
                    <p className="setting-description">Make location field mandatory when creating or editing assets</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.requireLocationForAssets}
                    onChange={(e) => updateSetting('requireLocationForAssets', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Auto-Generate Asset IDs</label>
                    <p className="setting-description">Automatically create unique identifiers for new assets</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.autoGenerateAssetIds}
                    onChange={(e) => updateSetting('autoGenerateAssetIds', e.target.checked)}
                  />
                </div>
              </div>
            )}

            {activeSection === 'inspection' && (
              <div className="settings-section">
                <h3>Inspection Settings</h3>
                <p className="section-description">Configure inspection requirements and workflows</p>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Require Photos on Inspection</label>
                    <p className="setting-description">Mandate photo attachments for inspection completion</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.requirePhotosOnInspection}
                    onChange={(e) => updateSetting('requirePhotosOnInspection', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Allow Offline Inspections</label>
                    <p className="setting-description">Enable inspections without network connectivity (syncs when online)</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.allowOfflineInspections}
                    onChange={(e) => updateSetting('allowOfflineInspections', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Minimum Checkpoint Completion</label>
                    <p className="setting-description">Required percentage of checkpoints to complete inspection</p>
                  </div>
                  <div className="setting-input-group">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={settings.minCheckpointCompletionRate}
                      onChange={(e) => updateSetting('minCheckpointCompletionRate', parseInt(e.target.value) || 0)}
                      className="setting-number-input"
                    />
                    <span className="setting-unit">%</span>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'notifications' && (
              <div className="settings-section">
                <h3>Notification Settings</h3>
                <p className="section-description">Configure alert and notification preferences</p>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Notify on Asset Added</label>
                    <p className="setting-description">Send notifications when new assets are created</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.notifyOnAssetAdded}
                    onChange={(e) => updateSetting('notifyOnAssetAdded', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Notify on Inspection Complete</label>
                    <p className="setting-description">Send notifications when inspections are completed</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.notifyOnInspectionComplete}
                    onChange={(e) => updateSetting('notifyOnInspectionComplete', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Notify on High-Risk Findings</label>
                    <p className="setting-description">Immediate alerts for high-priority inspection findings</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.notifyOnHighRiskFindings}
                    onChange={(e) => updateSetting('notifyOnHighRiskFindings', e.target.checked)}
                  />
                </div>
              </div>
            )}

            {activeSection === 'access' && (
              <div className="settings-section">
                <h3>Access Control</h3>
                <p className="section-description">Manage permissions and approval workflows</p>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Restrict Asset Editing</label>
                    <p className="setting-description">Only facility managers can edit asset details</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.restrictAssetEditing}
                    onChange={(e) => updateSetting('restrictAssetEditing', e.target.checked)}
                  />
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <label className="setting-label">Require Approval for Asset Deletion</label>
                    <p className="setting-description">Asset deletions must be approved by a manager</p>
                  </div>
                  <input
                    type="checkbox"
                    className="setting-toggle"
                    checked={settings.requireApprovalForAssetDeletion}
                    onChange={(e) => updateSetting('requireApprovalForAssetDeletion', e.target.checked)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          {saveError && (
            <p className="settings-save-error" role="alert" style={{ color: '#ef4444', margin: '0 0 0.5rem' }}>
              {saveError}
            </p>
          )}
          <div className="footer-actions">
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={18} />
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitySettingsModal;
